import * as React from 'react'

// Shared back-button handler for overlays (Dialog, Sheet, FullScreenPage).
//
// The problem: when overlays of different types nest, the previous
// per-primitive implementations checked their own state-key in isolation.
// Popping past an inner overlay's history entry would leave the outer
// overlay's listener seeing a state object that *doesn't* contain its key —
// it treated that as "I should close" even though it shouldn't. One back
// press cascade-closed every overlay on the page.
//
// Fix: one global, monotonically-incrementing depth counter. Each overlay
// records its depth on mount and closes only when (a) it is the topmost
// currently-active overlay AND (b) the new history state's depth is
// strictly less than its own. Sibling/parent overlays whose depth is still
// >= the new state's don't close; only the topmost responds to back.
//
// IMPORTANT: this hook must be invoked from a component that is mounted
// only when the overlay is actually visible — i.e. INSIDE Radix's
// Presence/Portal, not in the wrapper component that always mounts when
// declared in JSX. Use the `<OverlayHistoryEffect>` helper component
// below to satisfy that constraint.

let nextDepth = 1
const ACTIVE = new Set()

export function useOverlayHistory(closeFn, enabled = true) {
  const closeFnRef = React.useRef(closeFn)
  React.useEffect(() => {
    closeFnRef.current = closeFn
  })

  React.useEffect(() => {
    if (!enabled) return
    if (typeof window === 'undefined') return

    const myDepth = nextDepth++
    let closedByBack = false

    window.history.pushState({ __overlayDepth: myDepth }, '', window.location.href)
    ACTIVE.add(myDepth)

    const onPopState = (event) => {
      // Only the topmost currently-active overlay responds to back —
      // siblings/parents that are still open ignore the event.
      const maxActive = ACTIVE.size ? Math.max(...ACTIVE) : 0
      if (myDepth !== maxActive) return
      const newDepth = event.state?.__overlayDepth ?? 0
      if (newDepth >= myDepth) return
      closedByBack = true
      closeFnRef.current?.()
    }

    window.addEventListener('popstate', onPopState)

    return () => {
      window.removeEventListener('popstate', onPopState)
      ACTIVE.delete(myDepth)
      // If we're closing programmatically (X button, route change, etc.)
      // and our pushed entry is still on top, consume it so a subsequent
      // back press doesn't pop into nothingness.
      if (!closedByBack && window.history.state?.__overlayDepth === myDepth) {
        window.history.replaceState(null, '', window.location.href)
      }
    }
  }, [enabled])
}

// Helper component: mount this *inside* the Portal/Content tree so the
// hook only fires when the overlay is actually rendered (not when its
// outer wrapper component is declared with open=false).
export function OverlayHistoryEffect({ closeFn, enabled = true }) {
  useOverlayHistory(closeFn, enabled)
  return null
}
